import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
    apiSearchHandovers,
    apiIncidentTrail,
    formatApiError,
} from "@/lib/api";
import { toast } from "sonner";
import { Search, Hash, ArrowRight, ClipboardList } from "lucide-react";
import { StatusPill } from "@/components/admin/AdminUI";

export function KeywordSearch({ projectDir }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);

    async function run(e) {
        e?.preventDefault?.();
        const query = q.trim();
        if (query.length < 2) return;
        setLoading(true);
        try {
            const data = await apiSearchHandovers(query);
            setResults(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div data-testid="keyword-search">
            <form onSubmit={run} className="flex gap-2 mb-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ntt-muted" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search summaries, item titles, details, and references"
                        data-testid="keyword-input"
                        className="h-10 pl-9"
                    />
                </div>
                <Button
                    type="submit"
                    disabled={loading || q.trim().length < 2}
                    data-testid="keyword-search-btn"
                    className="h-10 bg-[#0072BC] hover:bg-[#005a94] text-white"
                >
                    Search
                </Button>
            </form>

            {results === null ? (
                <div className="text-[13px] text-ntt-muted italic">
                    Type at least 2 characters and press Enter.
                </div>
            ) : results.total === 0 ? (
                <div className="text-[13px] text-ntt-muted italic">
                    No matches for “{q}”.
                </div>
            ) : (
                <div className="space-y-3" data-testid="keyword-results">
                    <div className="text-[12px] text-ntt-muted">
                        {results.total} handover(s) matched.
                    </div>
                    {results.results.map((r) => (
                        <ResultCard
                            key={r.handover.handover_id}
                            handover={r.handover}
                            matches={r.matches}
                            projectDir={projectDir}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function ResultCard({ handover, matches, projectDir }) {
    const project = projectDir[handover.project_id];
    return (
        <div
            data-testid={`result-${handover.handover_id}`}
            className="border border-ntt-border rounded-md bg-white p-4"
        >
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-[13px] font-medium text-[#070F26]">
                        {handover.shift_name} · {handover.handover_date}
                    </div>
                    <div className="text-[12px] text-ntt-muted truncate">
                        {project?.name || handover.project_code} ·{" "}
                        {handover.outgoing_user_name || "—"} →{" "}
                        {handover.incoming_user_name || "(open)"}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <StatusPill
                        label={handover.status}
                        tone={
                            handover.status === "Acknowledged"
                                ? "green"
                                : handover.status === "Submitted"
                                  ? "blue"
                                  : "gray"
                        }
                    />
                    <Link
                        to={`/handovers/${handover.handover_id}`}
                        data-testid={`open-result-${handover.handover_id}`}
                        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md border border-ntt-border text-[12px] font-medium text-[#0072BC] hover:bg-[#F4F6F9]"
                    >
                        Open
                        <ArrowRight className="h-3 w-3" />
                    </Link>
                </div>
            </div>
            <div className="mt-3 space-y-1.5">
                {matches.map((m, idx) => (
                    <div
                        key={`${m.type || "match"}-${m.field || "f"}-${idx}`}
                        data-testid={`match-${idx}`}
                        className="text-[12px] text-[#2B2F3A] border-l-2 border-[#0072BC] pl-3"
                    >
                        <span className="uppercase text-[10px] tracking-wider text-ntt-muted mr-2">
                            {m.type === "summary" ? "Summary" : m.field || "item"}
                        </span>
                        {m.item?.title && (
                            <span className="font-medium">{m.item.title} — </span>
                        )}
                        <span className="text-ntt-muted">{m.snippet}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function IncidentTrail({ projectDir }) {
    const [ref, setRef] = useState("");
    const [trail, setTrail] = useState(null);
    const [loading, setLoading] = useState(false);

    async function run(e) {
        e?.preventDefault?.();
        if (!ref.trim()) return;
        setLoading(true);
        try {
            const data = await apiIncidentTrail(ref.trim());
            setTrail(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div data-testid="incident-trail">
            <form onSubmit={run} className="flex gap-2 mb-4">
                <div className="relative flex-1">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ntt-muted" />
                    <Input
                        value={ref}
                        onChange={(e) => setRef(e.target.value)}
                        placeholder="Ticket reference, e.g. INC5001"
                        data-testid="trail-input"
                        className="h-10 pl-9 font-mono"
                    />
                </div>
                <Button
                    type="submit"
                    disabled={loading || !ref.trim()}
                    data-testid="trail-search-btn"
                    className="h-10 bg-[#0072BC] hover:bg-[#005a94] text-white"
                >
                    Trace
                </Button>
            </form>
            {trail === null ? (
                <div className="text-[13px] text-ntt-muted italic">
                    Enter a ticket reference to see its full timeline across
                    shifts.
                </div>
            ) : trail.trail.length === 0 ? (
                <div className="text-[13px] text-ntt-muted italic">
                    No entries found for “{trail.reference}”.
                </div>
            ) : (
                <div
                    className="border-l-2 border-[#DBEAF6] ml-2 space-y-4"
                    data-testid="trail-results"
                >
                    {trail.trail.map((t, idx) => {
                        const project = projectDir[t.project_id];
                        return (
                            <div key={`${t.handover_id || t.handover_date}-${idx}`} className="relative pl-5">
                                <span className="absolute left-[-7px] top-1 h-3 w-3 rounded-full bg-[#0072BC] border-2 border-white" />
                                <div className="text-[12px] text-ntt-muted">
                                    {t.handover_date} · {project?.name || t.project_code} ·{" "}
                                    {t.shift_name}{" "}
                                    <StatusPill
                                        label={t.status}
                                        tone={
                                            t.status === "Acknowledged"
                                                ? "green"
                                                : t.status === "Submitted"
                                                  ? "blue"
                                                  : "gray"
                                        }
                                    />
                                </div>
                                <div className="text-[13px] font-medium text-[#070F26] mt-1">
                                    {t.item.title}{" "}
                                    <span className="ml-1 text-[11px] font-medium text-ntt-muted">
                                        [{t.item.category}
                                        {t.item.priority && t.item.priority !== "Not applicable"
                                            ? ` · ${t.item.priority}`
                                            : ""}
                                        {" · "}
                                        {t.item.item_status}]
                                    </span>
                                </div>
                                {t.item.details && (
                                    <div className="text-[12px] text-[#2B2F3A] whitespace-pre-wrap mt-1">
                                        {t.item.details}
                                    </div>
                                )}
                                <Link
                                    to={`/handovers/${t.handover_id}`}
                                    data-testid={`trail-open-${t.handover_id}`}
                                    className="inline-flex items-center gap-1 mt-2 text-[12px] font-medium text-[#0072BC] hover:underline"
                                >
                                    Open handover
                                    <ArrowRight className="h-3 w-3" />
                                </Link>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
